import { Link } from 'react-router-dom';

import styled from 'styled-components';
import { ColorPalette } from 'theme/colors';

import { ReactComponent as CheckCircle } from 'assets/images/check-circle.svg';
import { ReactComponent as Chevron } from 'assets/images/chevron-small.svg';
import { ReactComponent as Clock } from 'assets/images/clock.svg';
import { ReactComponent as ExclamationCircle } from 'assets/images/exclamation-circle.svg';
import { ReactComponent as Group } from 'assets/images/group.svg';
import { ReactComponent as Guest } from 'assets/images/invite.svg';
import { ReactComponent as Invite } from 'assets/images/invite.svg';
import { ReactComponent as XCircle } from 'assets/images/x-circle.svg';

export const Card = styled.div`
  border-radius: 12px;
  background-color: ${ColorPalette.HEX.White};
  padding: 24px 0 0;
  box-shadow: 0 2px 12px 0 rgba(${ColorPalette.RGB.DeepTaupe2}, 0.16);
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  margin: 0 24px;
`;

export const Divider = styled.hr`
  width: 100%;
  height: 1px;
  background-color: ${ColorPalette.HEX.BrightGray};
  border: none;
`;

export const Footer = styled.div``;

export const CodeText = styled.span`
  margin-right: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: ${ColorPalette.HEX.DimGray};
`;

export const ClockIcon = styled(Clock)``;

export const CheckCircleIcon = styled(CheckCircle)``;

export const ExclamationCircleIcon = styled(ExclamationCircle)``;

export const XCircleIcon = styled(XCircle)``;

type StatusTextProps = {
  $status: string;
};

export const GroupIcon = styled(Group)`
  margin-right: 4px;
`;

export const GuestIcon = styled(Guest)`
  margin-right: 4px;
`;

export const StatusText = styled.span<StatusTextProps>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-left: auto;
  font-size: 10px;
  line-height: 1.6;
  color: ${({ $status }) => {
    switch ($status) {
      case 'Pending':
        return ColorPalette.HEX.OrangeWeb;
      case 'Expiring':
        return ColorPalette.HEX.RedRYB;
      case 'PreApproved':
        return ColorPalette.HEX.OrangeWeb;
      case 'Approved':
        return ColorPalette.HEX.OrangeWeb;
      case 'Paid':
        return ColorPalette.HEX.GreenPantone;
      case 'Confirmed':
        return ColorPalette.HEX.GreenPantone;
      case 'Reproved':
        return ColorPalette.HEX.RedRYB;
      case 'Expired':
        return ColorPalette.HEX.RedRYB;
      case 'Cancelled':
        return ColorPalette.HEX.LavenderGray;
      case 'PaymentFailure':
        return ColorPalette.HEX.RedRYB;
      case 'ErrorAtSending':
        return ColorPalette.HEX.RedRYB;

      default:
        return ColorPalette.HEX.LavenderGray;
    }
  }};

  & ${ClockIcon}, & ${CheckCircleIcon}, & ${ExclamationCircleIcon}, & ${XCircleIcon} {
    width: 10px;
    height: 10px;
    margin-right: 4px;
  }
`;

export const Info = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: ${ColorPalette.HEX.Manatee};
`;

export const MoreInfo = styled(Link)`
  font-size: 12px;
  font-weight: normal;
  line-height: 1.5;
  color: ${ColorPalette.HEX.OriolesOrange};
  text-decoration: none;
`;

export const InfoValue = styled.span`
  display: inline;
  font-size: 12px;
  line-height: 1.5;
  color: ${ColorPalette.HEX.CharlestonGreen};
`;

export const Traveler = styled.p`
  margin: 14px 0 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${ColorPalette.HEX.DimGray};
`;

export const TravelerStatus = styled.p`
  display: flex;
  align-items: center;
  margin: 14px 5px 20px;
  font-size: 12px;
  line-height: 1.5;
  color: ${ColorPalette.HEX.Manatee};
`;

type PolicyStatusProps = {
  $valid: boolean;
};

export const PolicyStatus = styled.p<PolicyStatusProps>`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;

  color: ${({ $valid }) => ($valid ? ColorPalette.HEX.GreenPantone : ColorPalette.HEX.RedRYB)};
`;

export const DropdownChevron = styled(Chevron)`
  width: 16px;
  height: 16px;
`;

type ToggleProps = {
  $detailsOpen: boolean;
};

export const ToggleDetails = styled.button<ToggleProps>`
  display: inline-block;
  margin: 16px 0 16px auto;
  background-color: ${ColorPalette.Transparent};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: normal;
  color: ${ColorPalette.HEX.DimGray};
  cursor: pointer;

  &:disabled {
    opacity: 0.2;
    cursor: unset;
  }

  & ${DropdownChevron} {
    width: 10px;
    height: 10px;
    margin-left: 8px;
    vertical-align: middle;
    transform: ${({ $detailsOpen }) => ($detailsOpen ? 'rotate(270deg)' : 'rotate(90deg)')};
  }
`;

export const InviteIcon = styled(Invite)``;

export const HelpLink = styled(Link)`
  display: block;
  width: 100%;
  margin: 10px 0;
  padding-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
  color: ${ColorPalette.HEX.OriolesOrange};
  text-decoration: none;
`;

export const Destination = styled.span`
  font-size: 16px;
  color: ${ColorPalette.HEX.Gunmetal};
  font-weight: 600;
`;

export const DestinationDetail = styled.span`
  font-size: 12px;
  letter-spacing: -0.2px;
  color: ${ColorPalette.HEX.Manatee};
`;

export const FlightDetails = styled.div`
  display: flex;
  margin: 8px 0 0;
`;
